import { Component, ElementRef, NgZone, ViewChild } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

// A breaker rolling in from the horizon to the sand: where it is in its run (0 at the horizon, 1 on
// the shore) comes from the loop, so the waves follow each other without a seam.
interface Wave {
  /** Its place in the train (0..1), so the waves are spread evenly over one run. */
  offset: number;
  /** The crest's own ripple along the shore. */
  phase: number;
  /** Where along the crest it glows: two slow waves of patches, so the light comes in pieces. */
  patch: number;
  patch2: number;
  /** 0 = the theme's blue, 1 = its cyan, in between for the rest. */
  hue: number;
}

// A grain of glowing plankton in the shallows or on the wet sand.
interface Spark {
  u: number;
  /** How far in it sits (0.45..1.08 of the run; past 1 is the wet sand). */
  d: number;
  r: number;
  flicker: number;
  hue: number;
  /** Its rank: the counter lights more of them, lowest rank first. */
  rank: number;
}

interface Star {
  x: number;
  y: number;
  r: number;
  a: number;
  twinkle: number;
}

// The lights are drawn at full resolution (owner: fine detail, not muffled dots); the night behind
// them is baked at half and scaled up.
const RES = 0.5;
const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;
// one wave's run from the horizon to the shore, in loop time (four waves' runs in one loop)
const RUN = LOOP / 4;
const WAVES = 6;
const SPARKS = 900;
// the waves' soft halos are drawn at this fraction of a page pixel and scaled up
const HALO = 0.33;
// the horizon and the shore line, as fractions of the board's height
const HZ = 0.5;
const SHORE = 0.84;

/**
 * Bioluminescence's hero board: a night shore where every breaking wave lights up. Waves roll in from
 * the horizon, dark until they rise and break, then glow in electric blue and cyan in patches, the
 * light running along the crest and spilling as a fading wash onto the wet sand, where grains of
 * plankton keep sparkling. The pointer, a finger or the downloads counter stir the water: brighter
 * breakers and many more sparks. The page's wallpaper carries the plankton sparks alone.
 */
@Component( {
  selector: 'app-hero-bioluminescence',
  // the night (sky, sea, sand) on its own canvas under the lights, painted once per size and colour:
  // redrawing it every frame was most of a frame's cost on wide screens (owner: "kasıyor")
  template: '<canvas #night class="hb-canvas" aria-hidden="true"></canvas><canvas #canvas class="hb-canvas" aria-hidden="true"></canvas>',
  styleUrls: [ './hero-bioluminescence.component.scss' ],
} )
export class HeroBioluminescenceComponent extends HeroMotifBase {

  @ViewChild( 'night', { static: true } ) nightRef!: ElementRef<HTMLCanvasElement>;

  private waves: Wave[] = [];
  private sparks: Spark[] = [];
  private stars: Star[] = [];
  private night: HTMLCanvasElement | null = null;
  private nightKey = '';
  private dot: HTMLCanvasElement | null = null;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    // owner: lighter on very wide screens (1 -> 0.75, 0.6 on ultra-wide ones), the grains stay fine
    const wide = typeof window !== 'undefined' && window.innerWidth >= 1800;
    this.wide = wide;
    // owner: "bir tık daha hafifletelim", three times (ultra-wide 0.6 -> 0.5 -> 0.42 -> 0.36, sparser
    // lace, fewer sparks, a smaller halo)
    this.res = wide ? 0.36 : 0.75;
    // Safari still stuttered on ultra-wide screens: there a steady 30 frames a second
    this.maxFps = wide ? 30 : 0;
    this.loop = LOOP;
  }

  protected colorsChanged(): void {
    this.night = null;
    this.dot = null;
  }

  protected build( rand: () => number ): void {
    // the waves' looks come from a fixed seed, so every screen shows the same sea
    const fixed = this.fixedRandom( 7 );
    this.waves = [];
    for ( let i = 0; i < WAVES; i++ ) {
      this.waves.push( {
        offset: i / WAVES, phase: fixed() * Math.PI * 2,
        patch: fixed() * Math.PI * 2, patch2: fixed() * Math.PI * 2, hue: fixed(),
      } );
    }
    this.sparks = [];
    for ( let i = 0; i < SPARKS; i++ ) {
      this.sparks.push( {
        // all over the sea, from just under the horizon to the wet sand, thicker toward the shore
        u: fixed(), d: 0.04 + Math.pow( fixed(), 0.75 ) * 1.04, r: 0.6 + fixed() * fixed() * 1.8,
        flicker: fixed() * Math.PI * 2, hue: fixed(), rank: fixed(),
      } );
    }
    const count = Math.round( this.width * this.height * HZ / 9000 );
    this.stars = [];
    for ( let i = 0; i < count; i++ ) {
      this.stars.push( {
        x: rand(), y: Math.pow( rand(), 1.3 ) * HZ * 0.95,
        r: 0.5 + rand() * rand() * 1.3, a: 0.2 + rand() * 0.5, twinkle: rand() * Math.PI * 2,
      } );
    }
    this.night = null;
    this.starPaths = [];
  }

  private starPaths: Path2D[] = [];
  private halo: HTMLCanvasElement | null = null;
  private wide = false;

  protected paint( ctx: CanvasRenderingContext2D ): void {
    const [ deep, blue, violet, cyan, foam ] = this.five();
    if ( !this.dot ) { this.dot = this.makeDot(); }
    if ( this.frozen ) {
      this.paintWallpaper( ctx, blue, cyan );
      return;
    }
    const W = this.width;
    const H = this.height;
    const e = this.energy;
    const t = this.time;

    // the sky, the sea and the sand: painted onto the night canvas under this one, once per size
    const key = `${ W }x${ H }`;
    if ( !this.night || this.nightKey !== key ) {
      this.night = this.bakeNight( deep, blue, violet );
      this.nightKey = key;
      const n = this.nightRef.nativeElement;
      n.width = this.night.width;
      n.height = this.night.height;
      n.getContext( '2d' )!.drawImage( this.night, 0, 0 );
    }

    // the stars over the sea, in four groups (one path each) twinkling out of step
    if ( !this.starPaths.length ) {
      this.starPaths = [ 0, 1, 2, 3 ].map( () => new Path2D() );
      this.stars.forEach( ( s, i ) => {
        const p = this.starPaths[ i % 4 ];
        p.moveTo( s.x * W + s.r, s.y * H );
        p.arc( s.x * W, s.y * H, s.r, 0, Math.PI * 2 );
      } );
    }
    this.starPaths.forEach( ( p, i ) => {
      ctx.fillStyle = this.rgba( [ 225, 235, 255 ], 0.32 * ( 0.6 + 0.4 * Math.sin( t * W0 * 13 + i * 1.7 ) ) );
      ctx.fill( p );
    } );

    ctx.globalCompositeOperation = 'lighter';
    const hz = H * HZ;
    const shore = H * SHORE;
    // the light gathers at the centre and fades toward the sides (owner, for wide screens): full over
    // the middle of the stage (by its height, UW = min(W, 2.1 H)), gone past 0.8 UW from the centre,
    // where nothing glowing is drawn at all
    const uw = Math.min( W, H * 2.1 );
    const cx = W / 2;
    const fall = ( x: number ) => 1 - this.smooth( uw * 0.42, uw * 0.8, Math.abs( x - cx ) );
    const xa = Math.max( 0, cx - uw * 0.8 );
    const xb = Math.min( W, cx + uw * 0.8 );
    // the waves' wide soft halos go to a small canvas, added on top once (owner: Safari stuttered on
    // ultra-wide screens; a soft halo loses nothing at a third of the resolution)
    const hs = this.wide ? HALO * 0.6 : HALO;
    const hw = Math.max( 1, Math.ceil( W * hs ) );
    const hh = Math.max( 1, Math.ceil( H * hs ) );
    if ( !this.halo ) { this.halo = document.createElement( 'canvas' ); }
    if ( this.halo.width !== hw || this.halo.height !== hh ) { this.halo.width = hw; this.halo.height = hh; }
    const hctx = this.halo.getContext( '2d' )!;
    hctx.setTransform( 1, 0, 0, 1, 0, 0 );
    hctx.clearRect( 0, 0, hw, hh );
    hctx.setTransform( hs, 0, 0, hs, 0, 0 );
    hctx.globalCompositeOperation = 'lighter';
    hctx.lineCap = 'round';
    hctx.lineJoin = 'round';
    // the waves, far to near; each one's light peaks as it breaks and fades as it washes up
    const order = this.waves
      .map( w => ( { w, d: ( ( t / RUN + w.offset ) % 1 ) } ) )
      .sort( ( a, b ) => a.d - b.d );
    for ( const { w, d } of order ) {
      const y0 = this.depthY( d, hz, shore );
      const amp = H * 0.012 * d * d;
      // a seamless loop (owner: "atlıyor gibi olmasın"): a wave fades in out of the horizon and is
      // gone completely by the end of its wash, so its wrap back to the horizon never shows
      const life = this.smooth( 0, 0.12, d ) * ( 1 - this.smooth( 0.9, 1, d ) );
      // dark until it rises, then the break (about 0.72..0.95), then the wash on the sand
      const lit = this.smooth( 0.55, 0.86, d ) * life;
      const tone = this.mix( blue, cyan, w.hue );
      // owner: more light at rest, without the charge (0.55 -> 0.8)
      const glow = lit * ( 0.8 + 0.5 * e );
      // the crest's own body: a faint line, so the far waves read as rows on the dark sea
      const body = ( 0.05 + 0.1 * d ) * life;
      const path = new Path2D();
      const lit2 = new Path2D();
      const step = 12;
      // the crest's height every `step` px, kept so the lace below reads it instead of recomputing
      const ys: number[] = [];
      const bend = Math.pow( d, 1.7 );
      for ( let x = -step; x <= W + step * 2; x += step ) {
        const y = y0 + bend * this.shoreBend( x ) + amp * Math.sin( x * 0.006 + w.phase + t * W0 * 2 ) + amp * 0.5 * Math.sin( x * 0.017 - t * W0 * 3 + w.phase * 2 );
        ys.push( y );
        if ( x === -step ) { path.moveTo( x, y ); } else { path.lineTo( x, y ); }
        // the glowing part of the crest only runs where the light reaches
        if ( x >= xa - step && x <= xb + step ) {
          if ( x - step < xa - step ) { lit2.moveTo( x, y ); } else { lit2.lineTo( x, y ); }
        }
      }
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = this.rgba( this.mix( deep, foam, 0.25 ), body );
      ctx.lineWidth = 1 + 2.5 * d;
      ctx.stroke( path );
      if ( glow < 0.01 ) { continue; }
      // the light along the crest comes in patches: a gradient across the board whose stops follow
      // two slow waves, so no two waves glow in the same places
      // one gradient per wave (full strength), each pass laid with its own alpha
      const g = ctx.createLinearGradient( xa, 0, xb, 0 );
      {
        const n = 14;
        for ( let k = 0; k <= n; k++ ) {
          const x = xa + k / n * ( xb - xa );
          const p = 0.5 + 0.5 * Math.sin( x * 0.0045 + w.patch + t * W0 ) * Math.sin( x * 0.0021 - w.patch2 - t * W0 );
          // owner: more colour: the light covers more of the crest and its hue wanders along it,
          // blue to cyan with a touch of violet
          const a = glow * Math.max( 0, p * 1.6 - 0.05 ) * fall( x );
          const hueAt = 0.5 + 0.5 * Math.sin( x * 0.0031 + w.patch2 + t * W0 * 2 );
          const ink = this.mix( this.mix( blue, cyan, hueAt ), violet, 0.25 * Math.max( 0, Math.sin( x * 0.0017 + w.patch ) ) );
          g.addColorStop( k / n, this.rgba( ink, Math.min( 1, a ) ) );
        }
      }
      const patches = ( target: CanvasRenderingContext2D, width: number, alpha: number ) => {
        target.globalAlpha = alpha;
        target.strokeStyle = g;
        target.lineWidth = width;
        target.stroke( lit2 );
        target.globalAlpha = 1;
      };
      // owner: less light on the waves, more in the sea
      // owner: a little thicker (22/8/1.6 -> 30/14/2.6)
      patches( hctx, 30 * d, 0.06 );
      patches( hctx, 14 * d, 0.13 );
      patches( ctx, 2.6 * d + 0.6, 0.5 );
      // the foam's lace: fine bright grains strung along the breaking crest where it glows, each held
      // to its place on the wave (a hash of its slot), so the detail rides with the water
      const wi = this.waves.indexOf( w );
      // lighter on wide screens (owner): the crest and its patches are read from coarse samples and
      // interpolated, not computed per grain
      const crestAt = ( x: number ) => {
        const f = ( x + step ) / step;
        const i = Math.floor( f );
        return ys[ i ] + ( ys[ i + 1 ] - ys[ i ] ) * ( f - i );
      };
      const PS = 24;
      const ps: number[] = [];
      for ( let x = 0; x <= W + PS * 2; x += PS ) {
        ps.push( Math.max( 0, ( 0.5 + 0.5 * Math.sin( x * 0.0045 + w.patch + t * W0 ) * Math.sin( x * 0.0021 - w.patch2 - t * W0 ) ) * 1.6 - 0.05 ) * fall( x ) );
      }
      const patchAt = ( x: number ) => {
        const f = x / PS;
        const i = Math.floor( f );
        return ps[ i ] + ( ps[ i + 1 ] - ps[ i ] ) * ( f - i );
      };
      const lace = [ new Path2D(), new Path2D(), new Path2D() ];
      const pitch = this.wide ? 5 : 2.4;
      const spread = 2 + 7 * d;
      for ( let s = Math.floor( xa / pitch ); s * pitch < xb; s++ ) {
        const x = s * pitch;
        const p = patchAt( x ) * glow;
        if ( p < 0.06 ) { continue; }
        for ( let k = 0; k < 2; k++ ) {
          const h = this.hash( s, wi * 7 + k );
          if ( h > p * 0.7 ) { continue; }
          const jx = ( this.hash( s, wi * 7 + k + 3 ) - 0.5 ) * pitch;
          // the grains sit on the crest and in the spray just in front of it
          const jy = ( this.hash( s, wi * 7 + k + 5 ) - 0.35 ) * spread;
          const sz = 0.5 + this.hash( s, wi * 7 + k + 9 ) * ( 0.6 + 0.9 * d );
          lace[ Math.min( 2, Math.floor( p * 3 ) ) ].rect( x + jx, crestAt( x ) + jy, sz, sz * 0.8 );
        }
      }
      const grain = this.mix( tone, [ 255, 255, 255 ], 0.45 );
      lace.forEach( ( path, i ) => {
        ctx.fillStyle = this.rgba( grain, 0.2 + i * 0.2 );
        ctx.fill( path );
      } );
      // the wash: the broken wave's foam spreads up the sand as a thin glowing sheet edged with lace
      if ( d > 0.84 ) {
        const k = this.smooth( 0.84, 1, d );
        const top = y0 - 2;
        const reach = H * 0.05 * k;
        // the sheet follows the crest's own curve (owner: no straight line under the waves)
        const sheetShape = new Path2D();
        const i0 = Math.max( 0, Math.floor( ( xa + step ) / step ) - 1 );
        const i1 = Math.min( ys.length - 1, Math.ceil( ( xb + step ) / step ) + 1 );
        for ( let i = i0; i <= i1; i++ ) {
          const x = -step + i * step;
          if ( i === i0 ) { sheetShape.moveTo( x, ys[ i ] ); } else { sheetShape.lineTo( x, ys[ i ] ); }
        }
        for ( let i = i1; i >= i0; i-- ) { sheetShape.lineTo( -step + i * step, ys[ i ] + reach ); }
        sheetShape.closePath();
        // the sheet fades toward the sides with the rest of the light
        const g = ctx.createLinearGradient( xa, 0, xb, 0 );
        for ( let q = 0; q <= 8; q++ ) {
          g.addColorStop( q / 8, this.rgba( tone, 0.08 * glow * ( 1 - k * 0.6 ) * fall( xa + q / 8 * ( xb - xa ) ) ) );
        }
        ctx.fillStyle = g;
        ctx.fill( sheetShape );
        const sheet = new Path2D();
        for ( let s = Math.floor( xa / 3 ); s * 3 < xb; s++ ) {
          const x = s * 3;
          if ( this.hash( s, wi * 13 ) > patchAt( x ) * 0.9 ) { continue; }
          const yy = crestAt( x ) + Math.pow( this.hash( s, wi * 13 + 1 ), 1.8 ) * reach;
          sheet.rect( x + this.hash( s, wi * 13 + 2 ) * 3, yy, 1, 0.8 );
        }
        ctx.fillStyle = this.rgba( grain, 0.5 * glow * ( 1 - k * 0.5 ) );
        ctx.fill( sheet );
      }
    }

    // the halos, added in one go
    ctx.drawImage( this.halo, 0, 0, hw, hh, 0, 0, hw / hs, hh / hs );

    // the plankton: sparks in the shallows and on the wet sand, more of them as the water is stirred.
    // Grouped by colour and by one of a few strengths, so the alpha is set a handful of times a frame.
    const shown = ( 0.7 + 0.3 * e ) * ( this.wide ? 0.55 : 1 );
    const groups: number[][] = [ [], [], [], [], [], [], [], [], [], [], [], [] ];
    for ( const s of this.sparks ) {
      const x = s.u * W;
      // fewer and fainter toward the sides, never none
      const side = 0.3 + 0.7 * fall( x );
      if ( s.rank > shown * side ) { continue; }
      const y = ( s.d <= 1 ? this.depthY( s.d, hz, shore ) : shore + ( s.d - 1 ) * H * 0.6 )
        + Math.pow( Math.min( 1, s.d ), 1.7 ) * this.shoreBend( x );
      // a steady faint glow, flaring now and then
      const f = 0.3 + 0.7 * Math.max( 0, Math.sin( t * W0 * 9 + s.flicker * 6 ) * 0.6 + Math.sin( t * W0 * 23 + s.flicker ) * 0.4 );
      // a spark near the pointer wakes too
      let boost = 0;
      if ( this.near > 0.01 ) {
        const dx = x - this.pointerX;
        const dy = y - this.pointerY;
        boost = this.near * Math.exp( -( dx * dx + dy * dy ) / ( 2 * 140 * 140 ) );
      }
      const a = Math.min( 1, f * ( 0.72 + 0.4 * e ) * ( 0.45 + 0.55 * Math.min( 1, s.d ) ) * side + boost * 0.6 );
      if ( a < 0.02 ) { continue; }
      // a small soft halo and a crisp bright core, flattened a little as it lies on the water
      // owner: the sea's plankton a little bigger (2.6 -> 3.2)
      const r = s.r * ( 0.6 + s.d ) * 3.2;
      groups[ ( s.hue < 0.5 ? 0 : 6 ) + Math.min( 5, Math.floor( a * 6 ) ) ].push( x - r, y - r * 0.6, r * 2, r * 1.2 );
    }
    groups.forEach( ( list, gi ) => {
      if ( !list.length ) { return; }
      const sprite = this.tinted( gi < 6 ? blue : cyan );
      ctx.globalAlpha = ( ( gi % 6 ) + 0.5 ) / 6;
      for ( let i = 0; i < list.length; i += 4 ) { ctx.drawImage( sprite, list[ i ], list[ i + 1 ], list[ i + 2 ], list[ i + 3 ] ); }
    } );
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    // the logo, the motto and the counter keep a soft clearing
    if ( this.blocks.length ) {
      ctx.globalCompositeOperation = 'destination-out';
      for ( const b of this.blocks ) {
        ctx.save();
        ctx.translate( b.x + b.w / 2, b.y + b.h / 2 );
        ctx.scale( b.w / 2 + 60, b.h / 2 + 40 );
        const g = ctx.createRadialGradient( 0, 0, 0, 0, 0, 1 );
        g.addColorStop( 0, 'rgba(0,0,0,0.35)' );
        g.addColorStop( 1, 'rgba(0,0,0,0)' );
        ctx.fillStyle = g;
        ctx.fillRect( -1, -1, 2, 2 );
        ctx.restore();
      }
      ctx.globalCompositeOperation = 'source-over';
    }
  }

  /** The page's wallpaper: the plankton sparks alone, scattered still down the page. */
  private paintWallpaper( ctx: CanvasRenderingContext2D, blue: RGB, cyan: RGB ): void {
    const W = this.width;
    const H = this.height;
    const rand = this.fixedRandom( 11 );
    const count = Math.round( W * H / 14000 );
    ctx.globalCompositeOperation = this.lightSurface ? 'source-over' : 'lighter';
    for ( let i = 0; i < count; i++ ) {
      const x = rand() * W;
      const y = rand() * H;
      const r = ( 1.2 + rand() * rand() * 3.5 ) * 3;
      ctx.globalAlpha = ( 0.25 + rand() * 0.55 ) * ( this.lightSurface ? 0.6 : 1 );
      ctx.drawImage( this.tinted( rand() < 0.5 ? blue : cyan ), x - r, y - r, r * 2, r * 2 );
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  /** The sky, the sea and the sand at this size: near-black above, a faint glow on the horizon. */
  private bakeNight( deep: RGB, blue: RGB, violet: RGB ): HTMLCanvasElement {
    const W = this.width;
    const H = this.height;
    const c = document.createElement( 'canvas' );
    c.width = Math.max( 1, Math.ceil( W * RES ) );
    c.height = Math.max( 1, Math.ceil( H * RES ) );
    const g = c.getContext( '2d' )!;
    g.scale( RES, RES );
    const black: RGB = [ 4, 6, 11 ];
    const hz = H * HZ;
    const shore = H * SHORE;
    const sky = g.createLinearGradient( 0, 0, 0, hz );
    sky.addColorStop( 0, this.rgba( black, 1 ) );
    sky.addColorStop( 0.75, this.rgba( this.mix( black, deep, 0.7 ), 1 ) );
    sky.addColorStop( 1, this.rgba( this.mix( deep, violet, 0.12 ), 1 ) );
    g.fillStyle = sky;
    g.fillRect( 0, 0, W, hz + 1 );
    // the sea runs into the wet sand with no edge (owner: no straight line under the waves): one
    // gradient from the horizon to the bottom, the sand's tone taking over softly past the shore
    const sandTone = this.mix( black, [ 30, 26, 24 ], 0.35 );
    const sea = g.createLinearGradient( 0, hz, 0, H );
    sea.addColorStop( 0, this.rgba( this.mix( deep, blue, 0.08 ), 1 ) );
    sea.addColorStop( 0.8, this.rgba( this.mix( black, deep, 0.35 ), 1 ) );
    sea.addColorStop( 1, this.rgba( this.mix( black, deep, 0.2 ), 1 ) );
    g.fillStyle = sea;
    g.fillRect( 0, hz, W, H - hz );
    // the sand: its edge follows the shore's gentle S (owner: a curve, not a straight line), soft
    // where the water has just been
    const sand = new Path2D();
    sand.moveTo( -10, H + 10 );
    for ( let x = -10; x <= W + 10; x += 8 ) { sand.lineTo( x, shore + this.shoreBend( x ) ); }
    sand.lineTo( W + 10, H + 10 );
    sand.closePath();
    const sg = g.createLinearGradient( 0, shore - H * 0.04, 0, H );
    sg.addColorStop( 0, this.rgba( this.mix( black, blue, 0.1 ), 1 ) );
    sg.addColorStop( 0.3, this.rgba( sandTone, 1 ) );
    sg.addColorStop( 1, this.rgba( black, 1 ) );
    g.save();
    g.filter = 'blur(5px)';
    g.fillStyle = sg;
    g.fill( sand );
    g.restore();
    // the horizon's faint glow, the light of the far breakers
    const glow = g.createRadialGradient( W * 0.62, hz, 0, W * 0.62, hz, W * 0.55 );
    glow.addColorStop( 0, this.rgba( blue, 0.12 ) );
    glow.addColorStop( 1, this.rgba( blue, 0 ) );
    g.save();
    g.translate( 0, hz );
    g.scale( 1, 0.12 );
    g.translate( 0, -hz );
    g.fillStyle = glow;
    g.fillRect( 0, hz - W * 0.6, W, W * 1.2 );
    g.restore();
    return c;
  }

  /** A soft round grain of light, white, tinted per colour when drawn. */
  private makeDot(): HTMLCanvasElement {
    const c = document.createElement( 'canvas' );
    c.width = c.height = 32;
    const g = c.getContext( '2d' )!;
    const r = g.createRadialGradient( 16, 16, 0, 16, 16, 16 );
    // a pin-sharp core and a quick fall-off into a faint halo
    r.addColorStop( 0, 'rgba(255,255,255,1)' );
    r.addColorStop( 0.08, 'rgba(255,255,255,1)' );
    r.addColorStop( 0.16, 'rgba(255,255,255,0.45)' );
    r.addColorStop( 0.4, 'rgba(255,255,255,0.1)' );
    r.addColorStop( 1, 'rgba(255,255,255,0)' );
    g.fillStyle = r;
    g.fillRect( 0, 0, 32, 32 );
    return c;
  }

  private tintCache = new Map<string, HTMLCanvasElement>();

  private tinted( c: RGB ): HTMLCanvasElement {
    const key = c.map( Math.round ).join( ',' );
    let s = this.tintCache.get( key );
    if ( !s ) {
      s = document.createElement( 'canvas' );
      s.width = s.height = 32;
      const g = s.getContext( '2d' )!;
      g.drawImage( this.dot!, 0, 0 );
      g.globalCompositeOperation = 'source-in';
      g.fillStyle = this.rgba( this.mix( c, [ 255, 255, 255 ], 0.2 ), 1 );
      g.fillRect( 0, 0, 32, 32 );
      this.tintCache.set( key, s );
    }
    return s;
  }

  /**
   * The shore's gentle S, in page px below the straight shore line at x: laid out by the stage's height
   * (UW = min(W, 2.1 H), centred), so every screen shows the same bend. The waves take it on as they
   * near the shore (scaled by their run), so they always arrive parallel to the sand.
   */
  private shoreBend( x: number ): number {
    const W = this.width;
    const H = this.height;
    const uw = Math.min( W, H * 2.1 );
    const u = ( x - W / 2 ) / uw;
    return H * ( 0.03 * Math.sin( u * Math.PI * 1.15 + 0.5 ) + 0.012 * Math.sin( u * Math.PI * 2.7 - 0.3 ) );
  }

  /** A wave's screen height at its place in the run: rows crowd at the horizon and spread near the shore. */
  private depthY( d: number, hz: number, shore: number ): number {
    return hz + ( shore - hz ) * Math.pow( Math.min( 1, Math.max( 0, d ) ), 1.7 );
  }

  /** A fixed pseudo-random value per slot, so a grain keeps its place frame to frame (a table, not a sin per call). */
  private readonly table = ( () => {
    const r = this.fixedRandom( 23 );
    const a = new Float32Array( 8192 );
    for ( let i = 0; i < a.length; i++ ) { a[ i ] = r(); }
    return a;
  } )();

  private hash( i: number, j: number ): number {
    return this.table[ ( i * 31 + j * 977 ) & 8191 ];
  }

  private smooth( a: number, b: number, x: number ): number {
    const k = Math.min( 1, Math.max( 0, ( x - a ) / ( b - a ) ) );
    return k * k * ( 3 - 2 * k );
  }

  /** The colour circle: deep navy, blue, violet, cyan, the pale foam; a fallback when there is none. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 14, 21, 38 ], [ 47, 123, 255 ], [ 143, 124, 255 ], [ 54, 212, 240 ], [ 215, 227, 242 ] ];
  }

  private fixedRandom( seed: number ): () => number {
    let s = seed * 9973 + 17;
    return () => {
      s = ( s * 16807 ) % 2147483647;
      return ( s - 1 ) / 2147483646;
    };
  }
}
